import { createPortal } from 'react-dom';
import type { RefObject } from 'react';
import { getOverlayRoot } from '../../../lib/overlayRoot';
import { LeagueSwitcherList } from './LeagueSwitcherList';
import { useAnchorRect } from './useAnchorRect';
import type { LeagueSwitcherMenuProps } from './types';

/** Desktop dropdown — portaled to body so overflow-hidden ancestors can't clip it. */
export function LeagueSwitcherDropdown({
  activeId,
  memberships,
  leagues,
  unreadByLeague,
  onClose,
  onSelect,
  onAll,
  anchorRef
}: LeagueSwitcherMenuProps & { anchorRef: RefObject<HTMLElement | null> }) {
  const rect = useAnchorRect(anchorRef, true);

  if (!rect || typeof document === 'undefined') return null;

  const gap = 8;
  const width = Math.min(320, Math.max(rect.width, 220));
  const left = Math.min(rect.left, window.innerWidth - width - 16);
  const spaceBelow = window.innerHeight - rect.bottom - gap - 16;
  const spaceAbove = rect.top - gap - 16;
  const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
  const maxHeight = Math.min(360, openUp ? spaceAbove : spaceBelow);

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="pointer-events-auto fixed inset-0 hidden md:block"
      />
      <div
        role="menu"
        aria-label="Your leagues"
        style={{
          position: 'fixed',
          top: openUp ? undefined : rect.bottom + gap,
          bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
          left,
          width,
          maxHeight: Math.max(120, maxHeight)
        }}
        className="pointer-events-auto hidden animate-pop-in flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-white p-2 shadow-card md:flex"
      >
        <div className="px-2 pb-1 pt-1">
          <p className="text-[10px] font-bold uppercase tracking-eyebrow text-ink3">Your leagues</p>
        </div>
        <div className="native-scroll no-scrollbar flex-1 space-y-0.5 overflow-y-auto">
          <LeagueSwitcherList
            activeId={activeId}
            memberships={memberships}
            leagues={leagues}
            unreadByLeague={unreadByLeague}
            onSelect={onSelect}
            onAll={onAll}
            compact
          />
        </div>
      </div>
    </>,
    getOverlayRoot()
  );
}
